import { useState, useEffect } from 'react';
import { Coins, Package, Clock, Layers, Percent, AlertTriangle } from 'lucide-react';
import { apiCall } from './useAutoSave';
import { fmtCena, fmtTrvani, SECTION_COLOR } from './receptUtils';

// Hero shrnutí polotovaru/produktu: alergeny + klíčové metriky "at a glance".
export default function RecipeHero({ recept, kalkulace, bomLength = 0, isDark, mutedColor, secondaryColor, isMobile, onPouzito }) {
    const [alergeny, setAlergeny] = useState([]);
    const isPro = recept?.typ === 'PRO';

    useEffect(() => {
        let alive = true;
        if (recept?.id) {
            apiCall(`/api/recepty/recepty/${recept.id}/alergeny`)
                .then(d => { if (alive) setAlergeny(Array.isArray(d) ? d : (d?.alergeny || [])); })
                .catch(() => { if (alive) setAlergeny([]); });
        }
        return () => { alive = false; };
    }, [recept?.id]);

    const casS = (recept?.sop?.kroky || []).reduce((s, k) => s + (k.cas_s || 0), 0);
    const pouzito = recept?.kusovniky_jako_ingredience_count ?? 0;
    const cena = kalkulace ? fmtCena(kalkulace.total_cost) : null;
    const jed = kalkulace?.jednotka_zkratka;

    const tiles = [
        { icon: Coins, value: cena || '—', unit: cena && jed ? `/${jed}` : '', label: 'náklad', accent: SECTION_COLOR },
        { icon: Package, value: bomLength, label: 'ingrediencí' },
        { icon: Clock, value: fmtTrvani(casS) || '—', label: 'příprava' },
    ];

    // Příplatek (ADD) se prodává taky — food cost z příplatku bez DPH (2026-10-25).
    if (isPro || recept?.typ === 'ADD') {
        tiles.push({
            icon: Percent, label: 'food cost',
            value: kalkulace?.food_cost_pct != null ? `${kalkulace.food_cost_pct.toFixed(0)}%` : '—',
            accent: kalkulace ? (kalkulace.food_cost_pct <= 30 ? '#10B981' : kalkulace.food_cost_pct <= 40 ? SECTION_COLOR : '#EF4444') : undefined,
        });
    }
    // Dlaždice „použito" patří ke každé položce, která může být ingrediencí —
    // u produktu ji dřív vytlačil food cost, takže u něj nebylo kde se proklikat.
    if (!isPro || pouzito > 0) {
        tiles.push({
            icon: Layers, value: `${pouzito}×`, label: 'použito',
            accent: pouzito === 0 ? SECTION_COLOR : undefined,
            onClick: pouzito > 0 && onPouzito ? onPouzito : null,
            title: pouzito > 0 ? 'Zobrazit, ve kterých recepturách se používá' : undefined,
        });
    }

    const tileBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)';
    const tileBorder = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';

    return (
        <div className="px-4 py-3 space-y-3" style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}` }}>
            {alergeny.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                    <AlertTriangle size={13} style={{ color: '#EF4444', flexShrink: 0 }} />
                    <span className="text-[11px] font-medium" style={{ color: secondaryColor }}>Alergeny:</span>
                    {alergeny.map((a, i) => (
                        <span key={a.id || a.kod || i} className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                            style={{ background: 'rgba(239,68,68,0.12)', color: '#EF4444' }}>{a.nazev || a.kod || a}</span>
                    ))}
                </div>
            )}
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${isMobile ? 2 : tiles.length}, minmax(0, 1fr))` }}>
                {tiles.map((t, i) => {
                    const Icon = t.icon;
                    const Wrap = t.onClick ? 'button' : 'div';
                    return (
                        <Wrap key={i} onClick={t.onClick || undefined} title={t.title}
                            className="rounded-lg px-2.5 py-2 flex items-center gap-2 w-full"
                            style={{ background: tileBg, border: `1px solid ${tileBorder}`, textAlign: 'left', cursor: t.onClick ? 'pointer' : 'default', transition: 'border-color 0.15s' }}
                            onMouseEnter={e => { if (t.onClick) e.currentTarget.style.borderColor = SECTION_COLOR; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = tileBorder; }}>
                            <Icon size={16} style={{ color: t.accent || mutedColor, flexShrink: 0 }} />
                            <div className="min-w-0">
                                <div className="text-[13px] font-bold leading-tight truncate"
                                    style={{ color: t.accent || 'var(--text-primary)', textDecoration: t.onClick ? 'underline' : 'none' }}>
                                    {t.value}{t.unit ? <span className="text-[10px] font-normal" style={{ color: mutedColor }}>{t.unit}</span> : null}
                                </div>
                                <div className="text-[9px]" style={{ color: mutedColor }}>{t.label}</div>
                            </div>
                        </Wrap>
                    );
                })}
            </div>
        </div>
    );
}
